import { Link } from "react-router-dom";

import { NV_PATHS, NvButton, NvCompareIcon, NvDocumentIcon } from "@/components/navigator";
import { NvHeroPictogram, NvInfoRingIcon, NvLockBadgeIcon, NvPlayIcon } from "@/components/navigator/nv-icons-internal";
import { cn } from "@/lib/utils";
import type { AgentDetail } from "@/services/navigator/agent-detail-service";
import type { InternalDetailData } from "@/services/navigator/internal-detail-types";

import { finderPathOf, type HeaderActions } from "./agent-detail-header";
import { NvStarOutlineIcon } from "./detail-icons";
import { DETAIL_GUTTER } from "./detail-parts";
import { InternalTag, PwcMark } from "./internal-parts";

// 내부 Asset 상세 헤더 (Figma ver.7 03-09~12 · 03-09-2): 브레드크럼 → 왼쪽(PwC 내부 Asset · 소속 프로젝트 / 제품명 + 보안등급 배지 / 한 줄 소개 /
// 주황 CTA + Agent 정의서 / 저장 · 비교함) + 오른쪽 대표 이미지(4:3, 기본형 · MVP 이상은 데모 영상 칩) → 분류 띠 4종 → 바로가기.
// 왼쪽 묶음은 대표 이미지(354) 높이 안에서 세로 가운데예요.

export function InternalBreadcrumb({ detail }: { detail: AgentDetail }) {
  const trail = detail.internal?.breadcrumb ?? [];
  const sep = <span className="px-[5px]">/</span>;
  return (
    <nav aria-label="경로" className="text-[14px] leading-[22px] text-nv-text-tertiary">
      <Link to={NV_PATHS.agentFinder} className="hover:text-nv-text-primary">
        Agent Finder
      </Link>
      {trail[0] && (
        <>
          {sep}
          <span>{trail[0]}</span>
        </>
      )}
      {trail[1] && (
        <>
          {sep}
          <Link to={finderPathOf(detail)} className="hover:text-nv-text-primary">
            {trail[1]}
          </Link>
        </>
      )}
      {sep}
      <span>{detail.name}</span>
    </nav>
  );
}

// 보안등급 배지: 기밀 = 회색 반투명 + 자물쇠, 옆 정보 아이콘에 마우스를 올리면 설명 툴팁 (03-09-01-01)
function SecurityBadge({ security }: { security: InternalDetailData["security_code"] }) {
  if (security !== "confidential") return null;
  return (
    <>
      <span className="inline-flex h-[30px] items-center gap-[5px] rounded-[8px] bg-[#6B7280]/[0.49] px-[11px] text-[13px] font-medium leading-5 text-white">
        <NvLockBadgeIcon />
        기밀
      </span>
      <span className="group relative inline-flex">
        <button type="button" aria-label="보안등급 설명" aria-describedby="agent-security-tip" className="inline-flex text-nv-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand">
          <NvInfoRingIcon />
        </button>
        <span
          id="agent-security-tip"
          role="tooltip"
          className="pointer-events-none absolute left-1/2 top-full z-40 mt-2 hidden w-[388px] max-w-[calc(100vw-40px)] -translate-x-[22%] flex-col rounded-[10px] bg-nv-surface-inverse px-4 py-3 group-focus-within:flex group-hover:flex"
        >
          <span className="text-[13px] font-bold leading-5 text-white">보안등급 기밀</span>
          <span className="whitespace-nowrap text-[13px] leading-5 text-[#E3E5E8]">열람 승인을 받은 사용자에게만 상세 정보가 공개되는 Asset입니다.</span>
        </span>
      </span>
    </>
  );
}

// 대표 이미지: 4:3 기본형(그라데이션 + 픽토그램 + PwC 로고). 데모 영상이 있으면(MVP 이상) 로고는 오른쪽 위로, 왼쪽 아래에 재생 칩.
function HeroImage({ videoUrl, onPlay, blurred }: { videoUrl: string | null; onPlay: () => void; blurred?: boolean }) {
  const body = (
    <>
      <NvHeroPictogram className="absolute left-[184px] top-[125px]" />
      {videoUrl ? (
        <>
          <PwcMark width={70} className="absolute right-[28px] top-[23px]" />
          <span className="absolute bottom-[24px] left-[24px] flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-nv-surface-inverse text-white">
              <NvPlayIcon />
            </span>
            <span className="flex flex-col">
              <span className="text-[14px] leading-[22px] text-nv-text-primary">시연/데모 영상</span>
              <span className="text-[13px] leading-5 text-nv-text-secondary">클릭 시 재생</span>
            </span>
          </span>
        </>
      ) : (
        <PwcMark width={70} className="absolute bottom-[33px] left-[33px]" />
      )}
    </>
  );
  const cls = cn(
    "relative block h-[300px] w-full shrink-0 overflow-hidden rounded-[20px] border border-nv-border bg-[linear-gradient(135deg,#F3F4F6_14%,#FFF3EA_86%)] lg:h-[354px] lg:w-[472px]",
    blurred && "blur-[6px]",
  );
  if (videoUrl && !blurred) {
    return (
      <button type="button" onClick={onPlay} aria-label="시연/데모 영상 재생" className={cn(cls, "cursor-pointer text-left")}>
        {body}
      </button>
    );
  }
  return (
    <div aria-hidden={blurred} className={cls}>
      {body}
    </div>
  );
}

const ghostButton =
  "inline-flex h-11 items-center gap-[7px] rounded-nv-control border border-nv-border bg-nv-bg-subtle px-[15px] text-[14px] leading-[22px] hover:border-nv-border-strong";

export function InternalHeader({ detail, actions, onPlay }: { detail: AgentDetail; actions: HeaderActions; onPlay: () => void }) {
  const internal = detail.internal!;
  const restricted = internal.restricted;
  return (
    <section className={cn(DETAIL_GUTTER, "pb-14 pt-[28px] xl:pt-[28px]")}>
      <InternalBreadcrumb detail={detail} />
      <div className="mt-[28px] flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-12">
        <div className="flex min-w-0 flex-1 flex-col lg:max-w-[616px]">
          <div className="flex min-h-[24px] flex-wrap items-center gap-[10px]">
            <PwcMark width={41} className="mr-[7px]" />
            <span className="text-[14px] leading-[22px] text-nv-text-secondary">PwC 내부 Asset</span>
            {internal.project_name && (
              <>
                <span aria-hidden className="h-[14px] w-px bg-nv-border-strong" />
                <a href="#agent-project" className="text-[14px] leading-[22px] text-nv-text-secondary underline underline-offset-2 hover:text-nv-text-primary">
                  {internal.project_name}
                </a>
                <InternalTag />
              </>
            )}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <h1 className="break-keep text-[40px] font-normal leading-[46px] text-nv-text-primary xl:text-[52px] xl:leading-[60px]">{detail.name}</h1>
            <SecurityBadge security={internal.security_code} />
            {internal.stage_label && <InternalTag />}
          </div>
          {detail.summary && <p className="mt-5 line-clamp-3 max-w-[600px] break-keep text-[20px] leading-8 text-nv-text-primary">{detail.summary}</p>}
          {!restricted && (
            <>
              <div className="mt-5 flex flex-wrap items-center gap-[10px]">
                <NvButton asChild className="h-11 px-[21px] text-[15px]">
                  <Link to={actions.inquiryPath} onClick={actions.onInquiry}>
                    PwC와 함께 도입 문의하기
                  </Link>
                </NvButton>
                <button
                  type="button"
                  onClick={actions.onDefinition}
                  className="inline-flex h-11 items-center gap-[4px] rounded-nv-control border-[1.5px] border-nv-text-primary bg-nv-surface pl-3 pr-[10px] text-[15px] font-bold leading-6 text-nv-text-primary hover:bg-nv-bg-page"
                >
                  <NvDocumentIcon size={16} />
                  Agent 정의서 보기
                </button>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button type="button" onClick={actions.onBookmark} aria-pressed={detail.is_bookmarked} className={cn(ghostButton, detail.is_bookmarked ? "text-nv-brand-text" : "text-nv-text-secondary")}>
                  <NvStarOutlineIcon filled={detail.is_bookmarked} />
                  저장
                </button>
                <button type="button" onClick={actions.onCompare} aria-pressed={detail.in_compare} className={cn(ghostButton, detail.in_compare ? "text-nv-brand-text" : "text-nv-text-secondary")}>
                  <NvCompareIcon size={16} />
                  {detail.in_compare ? "비교함 담김" : "비교함 담기"}
                </button>
              </div>
            </>
          )}
        </div>
        <div className="flex min-w-0 flex-1 lg:flex-none">
          <HeroImage videoUrl={restricted ? null : internal.demo_video_url ?? null} onPlay={onPlay} blurred={restricted} />
        </div>
      </div>
    </section>
  );
}

// 분류 띠: 산업 및 Value Chain · 에이전트 유형 · 도입 형태 · 개발 단계(PwC 내부만, 보라 내부 전용 태그). 값이 빈 칸은 그 칸만 숨겨요.
export function InternalClassificationBar({ detail }: { detail: AgentDetail }) {
  const c = detail.classification;
  const stage = detail.internal?.stage_label;
  const items: { label: string; value: string | null | undefined; tip?: string | null; tag?: boolean }[] = [
    { label: "산업 및 Value Chain", value: c.industry },
    { label: "에이전트 유형", value: c.agent_type?.label, tip: c.agent_type?.definition },
    { label: "도입 형태", value: c.deployment?.label, tip: c.deployment?.definition },
    ...(stage ? [{ label: "개발 단계", value: stage, tag: true }] : []),
  ];
  return (
    <div className="border-y border-nv-border">
      <dl className={cn(DETAIL_GUTTER, "grid min-h-[89px] grid-cols-2 gap-x-6 gap-y-4 pb-5 pt-5 lg:grid-cols-4")}>
        {items.map((item) => (
          <div key={item.label} className="flex min-w-0 flex-col gap-1">
            {item.value ? (
              <>
                <dt className="flex items-center gap-[6px] text-[13px] leading-[21px] text-nv-text-tertiary">
                  {item.label}
                  {item.tip && (
                    <span title={item.tip} aria-label={`${item.label} 정의: ${item.tip}`} className="inline-flex cursor-help">
                      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 11v5.5" />
                        <circle cx="12" cy="7.9" r="1.1" fill="currentColor" stroke="none" />
                      </svg>
                    </span>
                  )}
                  {item.tag && <InternalTag />}
                </dt>
                <dd className="break-keep text-[15px] leading-6 text-nv-text-primary">{item.value}</dd>
              </>
            ) : null}
          </div>
        ))}
      </dl>
    </div>
  );
}

export interface InternalAnchor {
  key: string;
  label: string;
  tag?: boolean;
}

// 섹션 바로가기: 현재 섹션은 500 + 짙은 밑줄, 탭이 11개를 넘으면(12개부터) 13px · 간격 10으로 좁혀요 (Figma 03-10~12는 13px, 03-09는 14px)
export function InternalAnchorBar({ anchors, active, onGo }: { anchors: InternalAnchor[]; active: string | null; onGo: (key: string) => void }) {
  const compact = anchors.length > 11;
  return (
    <div id="agent-anchor-bar" className="sticky top-[var(--nv-gnb-height)] z-30 border-b border-nv-border bg-white/95 backdrop-blur-sm">
      <nav aria-label="섹션 바로가기" className={cn(DETAIL_GUTTER, "flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", compact ? "gap-[10px]" : "gap-6")}>
        {anchors.map((a) => (
          <button
            key={a.key}
            type="button"
            role="link"
            aria-current={active === a.key ? "location" : undefined}
            onClick={() => onGo(a.key)}
            className={cn(
              "inline-flex h-[52px] shrink-0 items-center gap-2 whitespace-nowrap border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand",
              compact ? "text-[13px] leading-5" : "text-[14px] leading-[22px]",
              active === a.key ? "border-nv-text-primary font-medium text-nv-text-primary" : "border-transparent text-nv-text-secondary hover:text-nv-text-primary",
            )}
          >
            {a.label}
            {a.tag && <InternalTag />}
          </button>
        ))}
      </nav>
    </div>
  );
}
